import { WorkflowReviewLookupRequestForm } from '@app/generated/contracts/workflow-review-lookup-request.generated';
import { applyWorkflowFormConfiguration } from '@app/shared/workflow-commands/workflow-form-configuration';
import {
  hasInstalledReviewRequestLookupRuntime,
  workflowFormFieldIssues,
} from './workflow-form-fields-validation';
import {
  isReviewRequestLookupCommand,
  reviewRequestLookupPairReason,
} from './workflow-review-request-lookup-authoring-gate';
import { captureFormDefinition } from '@app/shared/workflow-commands/workflow-capture-form';
import { isWorkflowFormVersionAvailable } from '@app/shared/workflow-commands/workflow-form-layout';
import { workflowCaptureCommandModule } from '@app/shared/workflow-commands/workflow-capture-command-adapter';
import { workflowReviewCommandModule } from '@app/shared/workflow-commands/workflow-review-command-adapters';
import { workflowCompleteCommandAdapter } from '@app/shared/workflow-commands/workflow-complete-command-adapter';
import { completionProviders } from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  providesVotingBlock,
  isHearingPanelPopulation,
  isCombinedVotingRound,
} from '@app/shared/workflow-voting/voting-sources';
import {
  noticePolicyDescription,
  noticeRequirementIssue,
} from '@app/phms/workflow-blocks/notice-policy';
import { WorkflowClientCommandRegistry } from '@app/shared/workflow-commands/workflow-command-registry';
import type { GeneratedFormDefinition } from '@template-ui/helpers/generated-form.helpers';
import type {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import type { WorkspaceAction } from '@app/workflow-workspace/workflow-workspace.component';
import { sameCapabilityReference } from './workflow-capability-authoring';
import {
  commandIdentity,
  type StepAuthoringCatalog,
  type StepCommand,
} from './workflow-step-composition';

export interface DetailSampleRequirement {
  id: string;
  label: string;
  description: string;
  formId?: string;
  formDefinition?: GeneratedFormDefinition;
  source?: StepCommand;
  unavailable: string;
}

export interface DetailSampleJourney {
  mode: 'step' | 'action';
  requirements: DetailSampleRequirement[];
  configurationIssues: string[];
  destination: { label: string; terminal: boolean } | null;
  actionBehaviors: {
    command: WorkflowCapabilityReference;
    label: string;
    completesStep: boolean | undefined;
    requiresForm: boolean;
    outcomeField: string | undefined;
    outcomes: StepCommand['outcomes'];
  }[];
}

const samplePayloadVersions = [
  '1.8',
  '1.9',
  '1.10',
  '1.11',
  '1.12',
  '1.13',
  '1.14',
  '1.15',
  '1.16',
  '1.17',
  '1.18',
  '1.19',
  '1.20',
  '1.21',
];
const clientForms = new WorkflowClientCommandRegistry([
  workflowReviewCommandModule,
  workflowCaptureCommandModule,
]);
const nonblank = (value: unknown, maximum = 120): value is string =>
  typeof value === 'string' && !!value.trim() && value.length <= maximum;
const hasOnly = (settings: Record<string, unknown>, keys: string[]) =>
  Object.keys(settings).every((key) => keys.includes(key));

/** Staff action responses expose the input contract; the exact authored reference is pinned against the catalog separately. */
export function sampleActionMatches(
  command: WorkspaceAction['command'],
  reference: WorkflowCapabilityReference,
): boolean {
  return (
    command.key.provider === reference.key.provider &&
    command.key.kind === reference.key.kind &&
    command.key.code === reference.key.code &&
    command.key.version === reference.key.version &&
    command.inputSchemaHash === reference.inputSchemaHash &&
    command.contractHash === reference.contractHash
  );
}

/** Projects supported authored bindings for a hypothetical visit; it never evaluates or manufactures live evidence. */
export function detailSampleJourney(
  document: WorkflowDefinitionDocument,
  node: WorkflowDefinitionNode,
  catalog: StepAuthoringCatalog | null,
  actions: WorkspaceAction[],
): DetailSampleJourney {
  const commands = node.declaration?.behavior.commands ?? [];
  const bindings = node.declaration?.behavior.completionRequirements ?? [];
  const registered = (command: StepCommand) => {
    const matches =
      catalog?.commands.filter((item) =>
        sameCapabilityReference(item.capability, command.capability),
      ) ?? [];
    return matches.length === 1 &&
      commands.filter(
        (item) => commandIdentity(item) === commandIdentity(command),
      ).length === 1
      ? matches[0]
      : null;
  };
  const actionForm = (command: StepCommand) => {
    if (
      !isWorkflowFormVersionAvailable(
        document.payloadSchemaVersion,
        command.captureForm,
      ) ||
      !isWorkflowFormVersionAvailable(
        document.payloadSchemaVersion,
        command.formConfiguration,
      )
    )
      return null;
    const matches = actions.filter((action) =>
      sampleActionMatches(action.command, command.capability),
    );
    const interaction = node.declaration?.detail.mainInteraction;
    const main =
      interaction &&
      commandIdentity({ capability: interaction.command }) ===
        commandIdentity(command)
        ? interaction
        : null;
    if (isReviewRequestLookupCommand(command.capability)) {
      const installed = registered(command);
      const form = matches.length === 1 ? matches[0].form : null;
      const configuration = command.formConfiguration;
      const interactions =
        catalog?.interactions.filter(
          (item) =>
            commandIdentity({ capability: item.command }) ===
            commandIdentity(command),
        ) ?? [];
      // Local validation uses only the exact saved lookup-free form, never a runtime offer.
      if (
        !samplePayloadVersions.includes(document.payloadSchemaVersion) ||
        !installed ||
        !hasInstalledReviewRequestLookupRuntime(command, installed) ||
        reviewRequestLookupPairReason(
          document,
          node.id,
          command,
          catalog,
          installed,
        ) ||
        !main ||
        main.code !== 'generated-command-form' ||
        main.version !== 1 ||
        !sameCapabilityReference(main.command, command.capability) ||
        interactions.length !== 1 ||
        !sameCapabilityReference(interactions[0].command, command.capability) ||
        interactions[0].code !== main.code ||
        interactions[0].version !== main.version ||
        !form ||
        matches[0].command.outputSchemaHash !==
          command.capability.outputSchemaHash ||
        form.contract !== WorkflowReviewLookupRequestForm.contract ||
        form.version !== WorkflowReviewLookupRequestForm.version ||
        form.inputSchemaHash !== command.capability.inputSchemaHash ||
        form.captureForm != null ||
        JSON.stringify(form.configuration ?? null) !==
          JSON.stringify(configuration ?? null) ||
        (configuration != null &&
          (![1, 2].includes(configuration.version) ||
            configuration.fields.some(
              (field) =>
                field.lookup != null || field.optionSource === 'lookup',
            )))
      )
        return null;
      const resolved = applyWorkflowFormConfiguration(
        WorkflowReviewLookupRequestForm,
        configuration,
        { installedDefinition: WorkflowReviewLookupRequestForm },
      );
      if (!resolved) return null;
      const issues = workflowFormFieldIssues(command, installed);
      return issues.form || Object.keys(issues.fields).length ? null : resolved;
    }
    return matches.length === 1
      ? clientForms.resolve(matches[0], main)?.form
      : null;
  };
  const requestSampleUnavailable = (command: StepCommand): boolean => {
    const installed = registered(command);
    if (!installed || !hasInstalledReviewRequestLookupRuntime(command, installed) || command.captureForm != null
      || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.formConfiguration)) return false;
    const matches = actions.filter(action => sampleActionMatches(action.command, command.capability)
      && action.command.outputSchemaHash === command.capability.outputSchemaHash);
    const form = matches.length === 1 ? matches[0].form : null;
    // Recognize presentation only. Request2 stays outside the executable sample registry.
    return !!form && form.contract === WorkflowReviewLookupRequestForm.contract && form.version === WorkflowReviewLookupRequestForm.version
      && form.inputSchemaHash === command.capability.inputSchemaHash && form.captureForm == null
      && JSON.stringify(form.configuration ?? null) === JSON.stringify(command.formConfiguration ?? null)
      && !!applyWorkflowFormConfiguration(WorkflowReviewLookupRequestForm, command.formConfiguration,
        { installedDefinition: WorkflowReviewLookupRequestForm });
  };
  const configurationIssues: string[] = [];
  if (document.nodes.filter((item) => item.id === node.id).length !== 1)
    configurationIssues.push('The selected step is ambiguous.');
  const complete = commands.filter(
    (command) =>
      command.capability.key.provider === 'workflow' &&
      command.capability.key.code === 'workflow.step.complete',
  );
  const mode = complete.length || bindings.length ? 'step' : 'action';
  if (
    mode === 'step' &&
    (complete.length !== 1 ||
      !workflowCompleteCommandAdapter.matches(complete[0].capability) ||
      !registered(complete[0])?.supportsCompletionRequirements ||
      !samplePayloadVersions.includes(document.payloadSchemaVersion))
  ) {
    configurationIssues.push(
      'A compatible Complete step action is needed to try completion.',
    );
  }
  for (const command of commands) {
    if (complete.includes(command)) continue;
    if (
      !registered(command) ||
      (registered(command)?.form && !actionForm(command))
    ) {
      configurationIssues.push(
        `${registered(command)?.label || command.capability.key.code}: ${requestSampleUnavailable(command)
          ? 'sample execution and checks are unavailable for this request form.'
          : 'a compatible action form is unavailable.'}`,
      );
    }
  }
  const requirements = bindings.map((binding) => {
    const row: DetailSampleRequirement = {
      id: binding.id,
      label: 'Required work',
      description: '',
      unavailable: '',
    };
    const contracts =
      catalog?.requirementProviders?.filter(
        (provider) =>
          provider.provider === binding.provider &&
          provider.version === binding.version &&
          provider.settingsVersion === binding.settingsVersion &&
          provider.settingsHash === binding.settingsHash,
      ) ?? [];
    if (
      ![
        '1.8',
        '1.9',
        '1.10',
        '1.11',
        '1.12',
        '1.13',
        '1.14',
        '1.15',
        '1.16',
        '1.17',
        '1.18', '1.19', '1.20', '1.21',
      ].includes(document.payloadSchemaVersion) ||
      bindings.filter((other) => other.id === binding.id).length !== 1 ||
      !nonblank(binding.id) ||
      contracts.length !== 1 ||
      !(
        (binding.version === 1 && binding.settingsVersion === 1) ||
        ([
          completionProviders.quorum,
          completionProviders.hearingGrantReview,
        ].includes(binding.provider as typeof completionProviders.quorum) &&
          binding.version === 2 &&
          binding.settingsVersion === 1) ||
        (binding.provider === completionProviders.sdmf &&
          binding.version === 2 &&
          binding.settingsVersion === 2)
      ) ||
      !binding.settings ||
      typeof binding.settings !== 'object' ||
      Array.isArray(binding.settings)
    ) {
      row.unavailable =
        'The saved requirement binding is unavailable or ambiguous.';
      return row;
    }
    const settings = binding.settings;
    const blocks = node.declaration?.detail.blocks ?? [];
    const domainChecks: Record<
      string,
      { label: string; description: string; code: string }
    > = {
      [completionProviders.hearingDecision]: {
        label: 'Hearing decision recorded',
        description:
          'Finalize the hearing decision with its certified SDMF and voting evidence.',
        code: 'hearing.decision',
      },
      [completionProviders.hearingConduct]: {
        label: 'Hearing outcome recorded',
        description:
          'Record the panel-supported grant or denial. Inconclusive votes return for further panel work.',
        code: 'hearing.decision',
      },
      [completionProviders.hearingGrantReview]: {
        label:
          binding.version === 2
            ? 'Legal review decision recorded'
            : 'Grant review approved',
        description:
          binding.version === 2
            ? 'Record an affirmative or negative legal review decision and notes for this hearing’s finalized grant.'
            : 'Approve the legal review attached to this hearing’s exact finalized grant.',
        code: 'hearing.grant-review',
      },
      [completionProviders.sdmf]: {
        label: 'Complete SDMF',
        description:
          'Complete and certify or confirm the preparation SDMF for this visit.',
        code: 'sdmf.analysis',
      },
      [completionProviders.hearingScheduled]: {
        label: 'Hearing scheduled',
        description: 'Book the hearing within its recorded scheduling dates.',
        code: 'scheduling.hearing',
      },
    };
    const domain = domainChecks[binding.provider];
    if (domain) {
      row.label = domain.label;
      row.description = domain.description;
      const matches = blocks.filter((b) => b.code === domain.code);
      if (
        matches.length !== 1 ||
        (binding.provider === completionProviders.hearingConduct &&
          matches[0].version !== 2) ||
        (binding.provider === completionProviders.hearingGrantReview &&
          matches[0].settings['sourceNodeId'] !== settings['sourceNodeId'])
      )
        row.unavailable =
          'Configure the matching hearing page block before trying this requirement.';
      return row;
    }
    if (
      [
        completionProviders.votingPopulation,
        completionProviders.votingRound,
        completionProviders.quorum,
      ].includes(
        binding.provider as typeof completionProviders.votingPopulation,
      ) &&
      (binding.provider !== completionProviders.quorum || binding.version === 2)
    ) {
      row.label =
        binding.provider === completionProviders.votingPopulation
          ? 'Voting population confirmed'
          : binding.provider === completionProviders.votingRound
            ? 'Voting round completed'
            : 'Quorum evaluated';
      row.description =
        'Use the confirmed panel and current visit’s recorded voting evidence.';
      const source = document.nodes.find(
        (n) => n.id === settings['sourceNodeId'],
      );
      if (
        binding.provider === completionProviders.quorum &&
        source?.declaration?.detail.blocks.some(isCombinedVotingRound)
      )
        row.label = 'Vote and quorum result recorded';
      if (
        binding.provider === completionProviders.votingPopulation &&
        source?.declaration?.detail.blocks.some(isHearingPanelPopulation)
      )
        row.label = 'Panel confirmed';
      const code =
        binding.provider === completionProviders.votingPopulation
          ? 'voting.population'
          : 'voting.ballots';
      if (
        !source?.declaration?.detail.blocks.some((b) =>
          providesVotingBlock(b, code),
        )
      )
        row.unavailable = 'Choose the step providing the voting evidence.';
      return row;
    }
    if (binding.provider === completionProviders.form) {
      row.label = 'Required form';
      const sources = commands.filter(
        (command) => command.captureForm?.id === settings['formId'],
      );
      const source = sources.length === 1 ? sources[0] : null;
      const contract = source ? registered(source) : null;
      if (
        !hasOnly(settings, ['formId']) ||
        !nonblank(settings['formId'], 80) ||
        !source ||
        !contract?.supportsCaptureForm ||
        contract.completesStep !== false ||
        source.capability.key.provider !== 'workflow' ||
        source.capability.key.code !== 'workflow.form.save' ||
        source.capability.key.version !== 1 ||
        !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, source.captureForm) ||
        !captureFormDefinition(source.captureForm) ||
        !actionForm(source)
      ) {
        row.unavailable =
          'Choose one compatible save-only form with valid fields.';
        return row;
      }
      row.label = source.captureForm!.title;
      row.formId = source.captureForm!.id;
      row.formDefinition = actionForm(source)!;
      row.source = source;
      row.description = 'Save all visible required answers for this visit.';
    } else if (binding.provider === completionProviders.document) {
      row.label = nonblank(settings['documentTypeLabel'])
        ? settings['documentTypeLabel']
        : 'Required document';
      const choices =
        contracts[0].settings.find(
          (setting) => setting.name === 'allowedFormats',
        )?.choices ?? [];
      const formats = settings['allowedFormats'];
      if (
        !hasOnly(settings, [
          'documentTypeCode',
          'documentTypeLabel',
          'sourceMode',
          'allowedFormats',
        ]) ||
        !nonblank(settings['documentTypeLabel']) ||
        !nonblank(settings['documentTypeCode']) ||
        !/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(
          settings['documentTypeCode'],
        ) ||
        settings['sourceMode'] !== 'new_visit' ||
        !Array.isArray(formats) ||
        !formats.length ||
        new Set(formats).size !== formats.length ||
        !formats.every((format) =>
          choices.some((choice) => choice.value === format),
        )
      ) {
        row.unavailable =
          'The saved document type or allowed formats are unavailable.';
        return row;
      }
      row.description = `Upload a new ${formats.map((format) => String(format).toUpperCase()).join(', ')} file for this visit.`;
    } else if (binding.provider === completionProviders.decision) {
      row.label = 'Recorded decision';
      const reference = settings['sourceCommand'] as
        | WorkflowCapabilityReference
        | undefined;
      const sources = reference?.key
        ? commands.filter((command) =>
            sameCapabilityReference(command.capability, reference),
          )
        : [];
      const source = sources.length === 1 ? sources[0] : null;
      const contract = source ? registered(source) : null;
      const evidence = contract?.decisionEvidence;
      const outcomes = settings['outcomeCodes'];
      if (
        !hasOnly(settings, ['sourceCommand', 'decisionKind', 'outcomeCodes']) ||
        !source ||
        !evidence ||
        contract?.completesStep !== false ||
        settings['decisionKind'] !== evidence.decisionKind ||
        !Array.isArray(outcomes) ||
        !outcomes.length ||
        new Set(outcomes).size !== outcomes.length ||
        !outcomes.every((outcome) => evidence.outcomeCodes.includes(outcome)) ||
        (!evidence.allowOutcomeSubset &&
          outcomes.length !== evidence.outcomeCodes.length) ||
        (evidence.requiredCommands ?? []).some((required) => {
          const matches = commands.filter((command) =>
            sameCapabilityReference(command.capability, required),
          );
          return matches.length !== 1 || !registered(matches[0]);
        })
      ) {
        row.unavailable =
          'The recorded decision action or supported results are unavailable.';
        return row;
      }
      row.description =
        'Record a supported result for this visit. Live review checks the reviewer’s identity and access.';
    } else if (binding.provider === completionProviders.notices) {
      row.label = 'Complete notice work';
      row.unavailable = noticeRequirementIssue(settings);
      const producers =
        node.declaration?.detail.blocks.filter(
          (b) =>
            b.provider === 'notices' &&
            b.code === 'notices.delivery' &&
            [1, 2].includes(b.version) &&
            b.settings['noticeGroupId'] === settings['noticeGroupId'] &&
            b.settings['policyJson'] === settings['policyJson'],
        ) ?? [];
      if (producers.length !== 1)
        row.unavailable = 'The matching notice delivery group is unavailable.';
      row.description = noticePolicyDescription(settings['policyJson']);
    } else row.unavailable = 'This requirement type has no sample preview.';
    return row;
  });
  const paths = document.edges.filter((edge) => edge.fromNodeId === node.id);
  const target =
    paths.length === 1 && !paths[0].outcomeCode && !paths[0].pathKindCode
      ? document.nodes.filter((item) => item.id === paths[0].toNodeId)
      : [];
  const destination =
    target.length === 1 &&
    ['human_step', 'terminal'].includes(target[0].typeCode)
      ? {
          label: target[0].displayName || target[0].stepCode || target[0].id,
          terminal: target[0].typeCode === 'terminal',
        }
      : null;
  const actionBehaviors = commands
    .filter((command) => !complete.includes(command))
    .map((command) => {
      const contract = registered(command);
      const action = actions.find((item) =>
        sampleActionMatches(item.command, command.capability),
      );
      return {
        command: command.capability,
        label: contract?.label || command.capability.key.code,
        completesStep: contract?.completesStep,
        requiresForm: !!contract?.form,
        outcomeField: action
          ? clientForms.resolve(action)?.adapter.outcomeField
          : undefined,
        outcomes: command.outcomes,
      };
    });
  return {
    mode,
    requirements,
    configurationIssues,
    destination,
    actionBehaviors,
  };
}
